import React from "react";
import ResourceManager from "./ResourceManager";

export function ManageCourses() {
  return (
    <ResourceManager
      title="Courses"
      endpoint="courses"
      defaults={{ title: "", slug: "", category: "long_term", duration: "", description: "", image_url: "", eligibility: "", highlights: [], icon: "GraduationCap", featured: false, active: true }}
      fields={[
        { name: "title", label: "Title", required: true },
        { name: "slug", label: "Slug (url-friendly)", required: true, placeholder: "mechatronics" },
        { name: "category", label: "Category", type: "select", required: true, options: [
          { value: "long_term", label: "Long Term" },
          { value: "short_term", label: "Short Term" },
          { value: "corporate", label: "Corporate" },
          { value: "earn_while_learn", label: "Earn While You Learn" },
        ] },
        { name: "duration", label: "Duration", required: true, placeholder: "3.5 years" },
        { name: "eligibility", label: "Eligibility", placeholder: "10th / 12th Pass" },
        { name: "icon", label: "Icon (lucide name)", placeholder: "Cog" },
        { name: "image_url", label: "Image", type: "image", col: "full", required: true },
        { name: "description", label: "Description", type: "textarea", col: "full", required: true, rows: 4 },
        { name: "highlights", label: "Highlights (one per line)", type: "array", col: "full" },
        { name: "featured", label: "Featured", type: "boolean" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Title" },
        { key: "category", label: "Category", render: (v) => (v || "").replace("_", " ") },
        { key: "duration", label: "Duration" },
        { key: "featured", label: "Featured" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageBanners() {
  return (
    <ResourceManager
      title="Banners"
      endpoint="banners"
      defaults={{ title: "", subtitle: "", image_url: "", cta_text: "Learn More", cta_link: "/admissions", order: 0, active: true }}
      fields={[
        { name: "title", label: "Title", required: true, col: "full" },
        { name: "subtitle", label: "Subtitle", type: "textarea", col: "full", rows: 2 },
        { name: "image_url", label: "Image", type: "image", col: "full", required: true },
        { name: "cta_text", label: "CTA Text" },
        { name: "cta_link", label: "CTA Link" },
        { name: "order", label: "Display Order", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Title" },
        { key: "order", label: "Order" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageFaculty() {
  return (
    <ResourceManager
      title="Faculty"
      endpoint="faculty"
      defaults={{ name: "", role: "", bio: "", image_url: "", order: 0, active: true }}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "role", label: "Role / Title", required: true },
        { name: "image_url", label: "Photo", type: "image", col: "full" },
        { name: "bio", label: "Bio", type: "textarea", col: "full", rows: 4 },
        { name: "order", label: "Display Order", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "order", label: "Order" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageNews() {
  return (
    <ResourceManager
      title="News & Events"
      endpoint="news"
      defaults={{ title: "", excerpt: "", content: "", image_url: "", date: new Date().toISOString().slice(0,10), active: true }}
      fields={[
        { name: "title", label: "Title", required: true, col: "full" },
        { name: "date", label: "Date", type: "date", required: true },
        { name: "image_url", label: "Image", type: "image" },
        { name: "excerpt", label: "Excerpt", type: "textarea", col: "full", rows: 2, required: true },
        { name: "content", label: "Full Content", type: "textarea", col: "full", rows: 6, required: true },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Title" },
        { key: "date", label: "Date" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageTestimonials() {
  return (
    <ResourceManager
      title="Testimonials"
      endpoint="testimonials"
      defaults={{ name: "", role: "", quote: "", image_url: "", rating: 5, active: true }}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "role", label: "Role / Position", required: true },
        { name: "image_url", label: "Photo", type: "image", col: "full" },
        { name: "quote", label: "Quote", type: "textarea", col: "full", rows: 4, required: true },
        { name: "rating", label: "Rating (1-5)", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "rating", label: "Rating" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManagePartners() {
  return (
    <ResourceManager
      title="Partners"
      endpoint="partners"
      defaults={{ name: "", role: "", logo_url: "", website: "", order: 0, active: true }}
      fields={[
        { name: "name", label: "Name", required: true },
        { name: "role", label: "Role / Tag", placeholder: "Training Partner" },
        { name: "logo_url", label: "Logo", type: "image", col: "full" },
        { name: "website", label: "Website URL", col: "full" },
        { name: "order", label: "Display Order", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "order", label: "Order" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageFinancialAid() {
  return (
    <ResourceManager
      title="Financial Aid"
      endpoint="financial-aid"
      defaults={{ title: "", description: "", eligibility: "", benefit: "", icon: "Wallet", order: 0, active: true }}
      fields={[
        { name: "title", label: "Title", required: true },
        { name: "icon", label: "Icon (lucide)", placeholder: "Wallet / Award / Landmark" },
        { name: "description", label: "Description", type: "textarea", col: "full", required: true },
        { name: "eligibility", label: "Eligibility", required: true },
        { name: "benefit", label: "Benefit", required: true },
        { name: "order", label: "Order", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Title" },
        { key: "benefit", label: "Benefit" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageVideos() {
  return (
    <ResourceManager
      title="Videos"
      endpoint="videos"
      defaults={{ title: "", description: "", youtube_id: "", thumbnail_url: "", order: 0, active: true }}
      fields={[
        { name: "title", label: "Title", required: true, col: "full" },
        { name: "youtube_id", label: "YouTube Video ID", required: true, placeholder: "dQw4w9WgXcQ" },
        { name: "order", label: "Display Order", type: "number" },
        { name: "thumbnail_url", label: "Custom Thumbnail (optional)", type: "image", col: "full" },
        { name: "description", label: "Description", type: "textarea", col: "full", rows: 3 },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Title" },
        { key: "youtube_id", label: "YouTube ID" },
        { key: "order", label: "Order" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageInstagram() {
  return (
    <ResourceManager
      title="Instagram Feed"
      endpoint="instagram"
      defaults={{ image_url: "", caption: "", post_url: "https://instagram.com", likes: 0, order: 0, active: true }}
      fields={[
        { name: "image_url", label: "Image", type: "image", required: true, col: "full" },
        { name: "post_url", label: "Post URL", col: "full" },
        { name: "caption", label: "Caption", type: "textarea", col: "full", rows: 2 },
        { name: "likes", label: "Likes Count", type: "number" },
        { name: "order", label: "Display Order", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "caption", label: "Caption" },
        { key: "likes", label: "Likes" },
        { key: "order", label: "Order" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageTicker() {
  return (
    <ResourceManager
      title="News Ticker"
      endpoint="ticker"
      defaults={{ message: "", link: "", order: 0, active: true }}
      fields={[
        { name: "message", label: "Message", required: true, col: "full", type: "textarea", rows: 2 },
        { name: "link", label: "Link (optional)", col: "full", placeholder: "/admissions" },
        { name: "order", label: "Display Order", type: "number" },
        { name: "active", label: "Active", type: "boolean" },
      ]}
      listColumns={[
        { key: "message", label: "Message" },
        { key: "link", label: "Link" },
        { key: "order", label: "Order" },
        { key: "active", label: "Active" },
      ]}
    />
  );
}

export function ManageBlog() {
  return (
    <ResourceManager
      title="Blog Posts"
      endpoint="blog"
      defaults={{ title: "", slug: "", excerpt: "", content: "", cover_image: "", author: "Kiki Centre", category: "General", tags: [], read_time: 5, date: new Date().toISOString().slice(0,10), published: true, featured: false }}
      fields={[
        { name: "title", label: "Title", required: true, col: "full" },
        { name: "slug", label: "URL Slug", required: true, placeholder: "my-blog-post-title" },
        { name: "category", label: "Category", type: "select", options: [
          { value: "Career Guide", label: "Career Guide" },
          { value: "Industry Trends", label: "Industry Trends" },
          { value: "Student Stories", label: "Student Stories" },
          { value: "Course Guide", label: "Course Guide" },
          { value: "General", label: "General" },
        ]},
        { name: "author", label: "Author" },
        { name: "date", label: "Publish Date", type: "date", required: true },
        { name: "read_time", label: "Read Time (minutes)", type: "number" },
        { name: "cover_image", label: "Cover Image", type: "image", col: "full", required: true },
        { name: "excerpt", label: "Excerpt (short summary)", type: "textarea", col: "full", rows: 2, required: true },
        { name: "content", label: "Full Content (supports ## headings, **bold**, - lists)", type: "textarea", col: "full", rows: 10, required: true },
        { name: "tags", label: "Tags (one per line)", type: "array", col: "full" },
        { name: "featured", label: "Featured", type: "boolean" },
        { name: "published", label: "Published", type: "boolean" },
      ]}
      listColumns={[
        { key: "title", label: "Title" },
        { key: "category", label: "Category" },
        { key: "author", label: "Author" },
        { key: "date", label: "Date" },
        { key: "published", label: "Published" },
      ]}
    />
  );
}
